import {
  TollingType,
  Unit,
  WorkOrderStatus,
} from '@rematter/frontend-lib-api-client';

import { createMutationAtomFactory } from '~/shared/providers/api/createMutationAtomFactory';

type CreateWorkOrderInputItem = {
  itemId: string;
  lotId?: string;
  tagId?: string;
  plannedQuantityUnit: Unit;
  plannedQuantity: number;
  notes?: string;
};

type CreateWorkOrderOutputItem = {
  itemId: string;
  targetLotId?: string;
  targetTagId?: string;
  expectedQuantityUnit: Unit;
  expectedQuantity: number;
  isNoCost: boolean;
  costAllocationPercentage?: number;
  notes?: string;
};

type UpdateWorkOrderInputItem = {
  id?: string | null;
  itemId: string;
  lotId?: string | null;
  tagId?: string | null;
  plannedQuantityUnit: Unit;
  plannedQuantity: number;
  notes?: string | null;
};

type UpdateWorkOrderOutputItem = {
  id?: string | null;
  itemId: string;
  targetLotId?: string | null;
  targetTagId?: string | null;
  expectedQuantityUnit: Unit;
  expectedQuantity: number;
  isNoCost?: boolean;
  costAllocationPercentage?: number | null;
  notes?: string | null;
};

export const createWorkOrderAtom = createMutationAtomFactory(
  'production.createWorkOrder',
  (params: {
    processingTypeId: string;
    facilityId: string;
    status: WorkOrderStatus;
    tollingType: TollingType;
    scheduledStartDate?: string;
    scheduledEndDate?: string;
    notes?: string;
    outboundTollingAgreementId?: string;
    tollCustomerAccountId?: string;
    inboundTollingAgreementId?: string;
    inputItems?: CreateWorkOrderInputItem[];
    outputItems?: CreateWorkOrderOutputItem[];
  }) => ({
    productionWorkOrderCreate: {
      __args: {
        input: {
          processingTypeId: params.processingTypeId,
          facilityId: params.facilityId,
          status: params.status,
          tollingType: params.tollingType,
          scheduledStartDate: params.scheduledStartDate,
          scheduledEndDate: params.scheduledEndDate,
          notes: params.notes,
          outboundTollingAgreementId: params.outboundTollingAgreementId,
          tollCustomerAccountId: params.tollCustomerAccountId,
          inboundTollingAgreementId: params.inboundTollingAgreementId,
          inputItems: params.inputItems,
          outputItems: params.outputItems,
        },
      },
      id: true,
      workOrderNumber: true,
      status: true,
    },
  }),
);

export const updateWorkOrderAtom = createMutationAtomFactory(
  'production.updateWorkOrder',
  (params: {
    id: string;
    processingTypeId?: string;
    scheduledStartDate?: string;
    scheduledEndDate?: string;
    notes?: string;
    tollingType?: TollingType;
    outboundTollingAgreementId?: string;
    tollCustomerAccountId?: string;
    inboundTollingAgreementId?: string;
    inputItems?: UpdateWorkOrderInputItem[];
    outputItems?: UpdateWorkOrderOutputItem[];
  }) => ({
    productionWorkOrderUpdate: {
      __args: {
        id: params.id,
        input: {
          processingTypeId: params.processingTypeId,
          scheduledStartDate: params.scheduledStartDate,
          scheduledEndDate: params.scheduledEndDate,
          notes: params.notes,
          tollingType: params.tollingType,
          outboundTollingAgreementId: params.outboundTollingAgreementId,
          tollCustomerAccountId: params.tollCustomerAccountId,
          inboundTollingAgreementId: params.inboundTollingAgreementId,
          inputItems: params.inputItems,
          outputItems: params.outputItems,
        },
      },
      id: true,
      workOrderNumber: true,
      status: true,
    },
  }),
);

export const openWorkOrderAtom = createMutationAtomFactory(
  'production.openWorkOrder',
  (params: { id: string }) => ({
    productionWorkOrderOpen: {
      __args: {
        id: params.id,
      },
      id: true,
      workOrderNumber: true,
      status: true,
    },
  }),
);

export const startWorkOrderAtom = createMutationAtomFactory(
  'production.startWorkOrder',
  (params: { id: string }) => ({
    productionWorkOrderStart: {
      __args: {
        id: params.id,
      },
      id: true,
      workOrderNumber: true,
      status: true,
    },
  }),
);

export const completeWorkOrderAtom = createMutationAtomFactory(
  'production.completeWorkOrder',
  (params: { id: string }) => ({
    productionWorkOrderComplete: {
      __args: {
        id: params.id,
      },
      id: true,
      workOrderNumber: true,
      status: true,
    },
  }),
);

export const cancelWorkOrderAtom = createMutationAtomFactory(
  'production.cancelWorkOrder',
  (params: { id: string; reason?: string }) => ({
    productionWorkOrderCancel: {
      __args: {
        id: params.id,
        reason: params.reason,
      },
      id: true,
      workOrderNumber: true,
      status: true,
    },
  }),
);

type CompleteWorkOrderInputItem = {
  inputItemId: string;
  actualQuantity: number;
  actualQuantityUnit: Unit;
  lotId?: string | null;
  tagId?: string | null;
};

type CompleteWorkOrderOutputItem = {
  outputItemId: string;
  actualQuantity: number;
  actualQuantityUnit: Unit;
  targetLotId?: string | null;
  targetTagId?: string | null;
};

export const completeWorkOrderWithActualsAtom = createMutationAtomFactory(
  'production.completeWorkOrderWithActuals',
  (params: {
    id: string;
    actualInputs: CompleteWorkOrderInputItem[];
    actualOutputs: CompleteWorkOrderOutputItem[];
  }) => ({
    productionWorkOrderCompleteWithActuals: {
      __args: {
        id: params.id,
        input: {
          actualInputs: params.actualInputs,
          actualOutputs: params.actualOutputs,
        },
      },
      id: true,
      workOrderNumber: true,
      status: true,
    },
  }),
);
